import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import '../styles/components/searchable-select.css';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Optional lowercase haystack for search. Falls back to `label` + `value`. */
  searchText?: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (_value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  /** Title used in the mobile bottom-sheet modal header. */
  modalTitle?: string;
  /** Extra class appended to the root wrapper. */
  className?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
}

const MOBILE_BREAKPOINT = 768;

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search',
  modalTitle = 'Select an option',
  className = '',
  disabled,
  id,
  name,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const selectedOption = useMemo(
    () => options.find(o => o.value === value),
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => {
      const haystack = o.searchText ?? `${o.label} ${o.value}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, query]);

  const closeAndReset = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setHighlightIndex(-1);
  }, []);

  const selectOption = useCallback(
    (val: string) => {
      onChange(val);
      closeAndReset();
    },
    [onChange, closeAndReset]
  );

  // Reset highlight whenever the filtered list changes while open.
  useEffect(() => {
    if (!isOpen) return;
    const selectedIdx = filtered.findIndex(o => o.value === value);
    setHighlightIndex(selectedIdx >= 0 ? selectedIdx : filtered.length > 0 ? 0 : -1);
  }, [filtered, isOpen, value]);

  // Keep the highlighted option visible.
  useEffect(() => {
    if (!isOpen || highlightIndex < 0) return;
    const el = itemRefs.current[highlightIndex];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex, isOpen]);

  // Focus search when opening.
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => searchInputRef.current?.focus(), 40);
    return () => clearTimeout(t);
  }, [isOpen]);

  // Click-outside dismiss (desktop/tablet only; the mobile modal uses an overlay).
  useEffect(() => {
    if (!isOpen || isMobile) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        closeAndReset();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isOpen, isMobile, closeAndReset]);

  // Lock body scroll while the mobile modal is open.
  useEffect(() => {
    if (!(isOpen && isMobile)) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen, isMobile]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        closeAndReset();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex(i => Math.min(filtered.length - 1, i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex(i => Math.max(0, i - 1));
        break;
      case 'Home':
        e.preventDefault();
        setHighlightIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setHighlightIndex(filtered.length - 1);
        break;
      case 'Enter': {
        e.preventDefault();
        const target = filtered[highlightIndex];
        if (target) selectOption(target.value);
        break;
      }
      default:
        break;
    }
  };

  const listboxId = id ? `${id}-listbox` : undefined;

  const renderSearch = () => (
    <div className="searchable-select-search-wrapper">
      <Search className="searchable-select-search-icon" aria-hidden="true" />
      <input
        ref={searchInputRef}
        type="text"
        className="searchable-select-search-input"
        placeholder={searchPlaceholder}
        value={query}
        onChange={e => setQuery(e.target.value)}
        aria-label={searchPlaceholder}
        aria-controls={listboxId}
        autoComplete="off"
        spellCheck={false}
      />
      {query && (
        <button
          type="button"
          className="searchable-select-search-clear"
          onClick={() => setQuery('')}
          aria-label="Clear search"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  const renderList = () => (
    <div className="searchable-select-list" role="listbox" id={listboxId}>
      {filtered.length === 0 ? (
        <div className="searchable-select-empty">No matches</div>
      ) : (
        filtered.map((opt, i) => {
          const isSelected = opt.value === value;
          const isHighlighted = i === highlightIndex;
          return (
            <button
              key={opt.value}
              ref={el => {
                itemRefs.current[i] = el;
              }}
              type="button"
              role="option"
              aria-selected={isSelected}
              className={`searchable-select-option${isSelected ? ' is-selected' : ''}${
                isHighlighted ? ' is-highlighted' : ''
              }`}
              onMouseEnter={() => setHighlightIndex(i)}
              onClick={() => selectOption(opt.value)}
            >
              <span className="searchable-select-option-label">{opt.label}</span>
              {isSelected && (
                <Check className="searchable-select-option-check" aria-hidden="true" />
              )}
            </button>
          );
        })
      )}
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={`searchable-select${isOpen ? ' is-open' : ''}${className ? ` ${className}` : ''}`}
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        id={id}
        name={name}
        className="searchable-select-trigger select-input"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        onClick={() => {
          if (disabled) return;
          setIsOpen(o => !o);
        }}
      >
        <span
          className={`searchable-select-value${selectedOption ? '' : ' is-placeholder'}`}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className="searchable-select-chevron" aria-hidden="true" />
      </button>

      {isOpen && !isMobile && (
        <div className="searchable-select-dropdown" role="dialog">
          {renderSearch()}
          {renderList()}
        </div>
      )}

      {isOpen && isMobile && (
        <div
          className="searchable-select-modal-overlay"
          role="presentation"
          onClick={closeAndReset}
        >
          <div
            className="searchable-select-modal"
            role="dialog"
            aria-modal="true"
            aria-label={modalTitle}
            onClick={e => e.stopPropagation()}
          >
            <div className="searchable-select-modal-header">
              <h3 className="searchable-select-modal-title">{modalTitle}</h3>
              <button
                type="button"
                className="searchable-select-modal-close"
                onClick={closeAndReset}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {renderSearch()}
            {renderList()}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
